import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { KMDSettingService } from '@app/core/services/kmdSetting.service';
import { KMDSetting } from '@app/shared/models/kmdSetting';
import { Role } from '@shared/models/enums';
import { isCompanyOrInternalType } from '@shared/utilities/association-role.util';
import { kmdDomainOf } from '@shared/utilities/kmd-booking';

import { KMD_OWN_PAGES } from './kmd-pages';

import { AssociationsStore } from './store/associations.store';

/**
 * Where the menu shows KMD: the one place that decides it, so the menu and the help never drift
 * apart. The help shows its guides to KMD's association pages exactly where the menu has the pages
 * (`HelpStore` asks {@link showOwnLinks}).
 *
 * The tenant switches the pages on in `kmd_setting` (`GET /kmdsettings`): `MenuLinksEnabled` for an
 * organisation login's own KMD pages and `PortalLinkEnabled` for KMD → Foreningsportal. Each needs the
 * KMD domain, so no link ever points at a bare path.
 */
@Injectable({ providedIn: 'root' })
export class KmdMenuLinksService {
  private readonly settingService = inject(KMDSettingService);
  private readonly associationsStore = inject(AssociationsStore);

  private readonly setting = signal<KMDSetting | null>(null);
  private loading: Promise<void> | null = null;

  /** The KMD domain without a trailing slash. Empty until `GET /kmdsettings` answers with one. */
  readonly domain = computed(() => kmdDomainOf(this.setting()));

  /** The tenant has the organisation's KMD pages in the menu. */
  readonly menuLinksEnabled = computed(() => !!this.domain() && !!this.setting()?.menuLinksEnabled);

  /** The tenant has KMD → Foreningsportal in the menu. */
  readonly portalLinkEnabled = computed(
    () => !!this.domain() && !!this.setting()?.portalLinkEnabled,
  );

  /** The selected organisation is a company or an internal customer, not an association. */
  readonly ownIsOrganisation = computed(() => this.associationsStore.selectedIsOrganisation());

  /**
   * The selected organisation's type — 15, 4 or 6, anything else counting as an association, like
   * `selectedIsOrganisation` — or null while the user's organisations are still being loaded, so no
   * type-bound page flashes into another type's menu meanwhile.
   */
  readonly ownType = computed((): Role | null => {
    if (this.associationsStore.selectedUserAssociationInfo() === null) return null;
    const typeId = this.associationsStore.selectedOrganisationTypeId();
    return isCompanyOrInternalType(typeId) ? (typeId as Role) : Role.Association;
  });

  /** KMD has own pages (Min forening / Min virksomhed) for the selected organisation's type — none for an internal customer. */
  readonly ownHasPages = computed(() => {
    const type = this.ownType();
    return type !== null && KMD_OWN_PAGES.some((page) => page.types?.includes(type));
  });

  /**
   * Fetches the tenant's KMD settings once, however many callers ask, and resolves when the answer
   * is in — so the help can wait for it rather than show "not found" for a guide that is about to
   * appear. A failed request leaves everything off — no KMD links is the safe answer for a menu and
   * for a guide that points at them — and is tried again by the next call, rather than settling
   * "off" for the rest of the session.
   */
  load(): Promise<void> {
    this.loading ??= firstValueFrom(this.settingService.get())
      .then((setting) => this.setting.set(setting))
      .catch(() => {
        this.setting.set(null);
        this.loading = null;
      });

    return this.loading;
  }

  /**
   * Whether the login gets its organisation's own KMD pages: those under Min forening, and Tilskud,
   * Dokumenter and Min konto. An organisation's people — an association's, a company's or an internal
   * customer's — and only where the tenant has them on. Which of the pages a role gets is then up to
   * its `view_kmd_*` permissions and to the organisation types KMD has the page for (`types` on the
   * menu's pages: a company's own pages differ from an association's, an internal customer has none,
   * and only an association has the Dokumenter group and Foreningsansøgninger).
   * @param associationLogin The login is an organisation's person (16, 17, 19–22), not an administrator
   */
  showOwnLinks(associationLogin: boolean): boolean {
    return associationLogin && this.menuLinksEnabled();
  }

  /**
   * Whether the login gets its organisation's own pages under Min forening / Min virksomhed: as
   * {@link showOwnLinks}, and KMD has such pages for the organisation's type ({@link ownHasPages}).
   * The help's `kmd_menu_links` asks this, as its guides are to those pages.
   */
  showOwnPages(associationLogin: boolean): boolean {
    return this.showOwnLinks(associationLogin) && this.ownHasPages();
  }

  /**
   * Whether the login gets KMD → Foreningsportal. An administrator always, where the tenant has it;
   * an association's people only while their own KMD pages are off, as Startside already leads
   * there; a company's or internal customer's people never — not even while their KMD pages are off,
   * as the portal is the associations'.
   * @param associationLogin The login is an organisation's person (`isAssociationLogin`)
   */
  showPortalLink(associationLogin: boolean): boolean {
    return (
      this.portalLinkEnabled() &&
      (associationLogin ? !this.ownIsOrganisation() && !this.menuLinksEnabled() : true)
    );
  }
}
